import React, {
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useLocalStorage from 'react-localstorage-hook';

import { Address } from 'viem';
import { Connector, useAccount, useConnect, useDisconnect } from 'wagmi';

import ViewAs from '@contexts/common/AuthorizationContext/ViewAs';
import { modalActions } from '@contexts/common/ModalContext';

import { isAddressesEqual } from '@utils/tokens.utils';

import {
  AVAILABLE_CHAINS_LIST,
  AvailableChainIdType,
} from '@constants/common/chain.constants';
import { WHITE_LIST_ADDRESS } from '@constants/common/common.constants';
import {
  CURRENT_CHAIN_ID,
  VIEW_ADDRESS,
} from '@constants/common/localStorage.constants';
import { MODAL_TYPES } from '@constants/common/modals.constants';
import { ConnectorNames } from '@constants/common/wallet.constants';

type AuthorizationContextType = {
  isConnected: boolean;
  isAuthReady: boolean;
  account: Address | undefined;
  isPending: boolean;
  chainId: AvailableChainIdType | undefined;
  connector: Connector | undefined;
};

type AuthorizationActionsType = {
  handleDisconnect: () => void;
  handleLogIn: (connectorId: ConnectorNames) => Promise<void>;
};

const AuthorizationContext = createContext<AuthorizationContextType>({
  isConnected: false,
  isAuthReady: false,
  account: undefined,
  isPending: false,
  chainId: undefined,
  connector: undefined,
});
export let authorizationActions: AuthorizationActionsType;

export const useAuth = (): AuthorizationContextType =>
  useContext(AuthorizationContext);

const AuthorizationContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [chainInStorage] = useLocalStorage(CURRENT_CHAIN_ID);
  const [viewAddress] = useLocalStorage(VIEW_ADDRESS);
  const [wasConnecting, setWasConnecting] = useState(false);

  const {
    chainId,
    isConnecting,
    connector,
    isConnected,
    isReconnecting,
    address,
    status,
  } = useAccount();

  const { connectAsync, isPending, connectors } = useConnect();
  const { disconnectAsync } = useDisconnect();

  const activeChainId = useMemo(
    () => chainId || Number(chainInStorage),
    [chainId, chainInStorage],
  );

  const isAuthReady =
    (status === 'connected' && !!address) ||
    (status === 'disconnected' && wasConnecting && !isConnecting);

  const isAdmin = useMemo(
    () => WHITE_LIST_ADDRESS.find((item) => isAddressesEqual(address, item)),
    [address],
  );

  authorizationActions = {
    handleDisconnect: async () => {
      await disconnectAsync();
    },
    handleLogIn: async (connectorId: ConnectorNames) => {
      const findConnector = connectors.find((c) => c.id === connectorId);

      if (!findConnector) return;

      await connectAsync({
        connector: findConnector,
        chainId: activeChainId,
      }).catch((e) => console.log(e));
    },
  };

  useEffect(() => {
    if (isConnected && !AVAILABLE_CHAINS_LIST.includes(chainId as number)) {
      modalActions.show(MODAL_TYPES.WRONG_NETWORK_MODAL);
    } else {
      modalActions.hide();
    }
  }, [isConnected, chainId]);

  useEffect(() => {
    if (status === 'connecting' || status === 'connected') {
      setWasConnecting(true);
    }
  }, [status]);

  return (
    <AuthorizationContext.Provider
      value={{
        isConnected: !!address,
        account: isAdmin && viewAddress ? viewAddress : address,
        isPending: isConnecting || isPending || isReconnecting,
        chainId: activeChainId,
        connector,
        isAuthReady,
      }}
    >
      {isAdmin && <ViewAs address={address} />}
      {children}
    </AuthorizationContext.Provider>
  );
};

export default AuthorizationContextProvider;
